import React, { useState, useEffect } from "react";
import styles from "./create.module.css";
import { Helmet } from "react-helmet";
import { Stepper } from "react-form-stepper";
import { Col, Row, Form, Button, Spinner } from "react-bootstrap";
import Header from "../../Component/Header/Header";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CREATE_FORMS,
  CREATE_RESEARCH,
  GET_CATEGORIES,
  GET_FORMS,
} from "../../APIs/BaseUrl";
import Notification from "../../Component/Notification/Notification";
import { useAuth } from "../../Context/AuthContext";

const RESEARCH_TYPES = [
  { label: "Online Study", value: "online study" },
  { label: "Group Study", value: "group study" },
  { label: "Detail Interview", value: "detail interview" },
  { label: "Central Location Test", value: "central location test" },
  { label: "Survey", value: "survey" },
  { label: "Diary Study", value: "diary study" },
];

const questionTypes = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "multiple-choice", label: "Multiple Choice" },
  { value: "dropdown", label: "Dropdown" },
  { value: "file", label: "File" },
];

const createEmptyQuestion = () => ({
  questionText: "",
  questionText_i18n: { en: "", hinglish: "" },
  questionType: "text",
  required: true,
  optionRows: [],
});

const buildQuestionPayload = (question) => {
  const englishText =
    question.questionText_i18n?.en?.trim() || question.questionText?.trim() || "";
  const hinglishText = question.questionText_i18n?.hinglish?.trim() || "";
  const isChoiceType = ["dropdown", "multiple-choice"].includes(question.questionType);

  const optionRows = isChoiceType
    ? (question.optionRows || []).map((option) => ({
        en: option.en?.trim() || "",
        hinglish: option.hinglish?.trim() || "",
      }))
    : [];

  return {
    questionText: englishText,
    questionText_i18n: {
      en: englishText,
      hinglish: hinglishText,
    },
    questionType: question.questionType,
    required: question.required !== false,
    options: optionRows.map((option) => option.en).filter(Boolean),
    options_i18n: optionRows.filter((option) => option.en || option.hinglish),
  };
};

const CreateProject = () => {
  const navigate = useNavigate();
  const {toggleNotification} = useAuth();
  
  const [step, setStep] = useState(0);

  // Form state
  const [form, setForm] = useState({
    research_name: "",
    type: "",
    description: "",
    is_age_limited: false,
    age_limit: { min_age: "", max_age: "" },
    duration: "",
    category: "",
    location: "",
    price: "",
    doc: "",
    status: true,
    is_form_applicable: false,
    max_applications: 0,
    form_questionair: "",
  });

  // Dropdown data
  const [categories, setCategories] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formMode, setFormMode] = useState("existing");
  const [newFormName, setNewFormName] = useState("");
  const [questions, setQuestions] = useState([createEmptyQuestion()]);

  // Fetch categories and forms
  useEffect(() => {
    // Replace with your API endpoints
    const token = localStorage.getItem("token");
    axios
      .get(`${GET_CATEGORIES}?limit=100&page=1`, {
      headers: { Authorization: `Bearer ${token}` },
      })
      .then(({data}) => setCategories(data?.data?.docs || []));

    axios
      .get(`${GET_FORMS}?limit=100&page=1`, {
      headers: { Authorization: `Bearer ${token}` },
      })
      .then(({data}) => setForms(data?.data?.docs || []));
  }, []);

  // Handlers
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (
      name === "is_age_limited" ||
      name === "status" ||
      name === "is_form_applicable"
    ) {
      setForm({ ...form, [name]: checked });
    } else if (name === "min_age" || name === "max_age") {
      setForm({ ...form, age_limit: { ...form.age_limit, [name]: value } });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleNext = () => setStep((prev) => prev + 1);
  const handleBack = () => setStep((prev) => prev - 1);

  const handleQuestionChange = (idx, field, value) => {
    const updated = [...questions];
    if (field === "questionTextEn") {
      updated[idx].questionText = value;
      updated[idx].questionText_i18n = {
        ...(updated[idx].questionText_i18n || {}),
        en: value,
      };
    } else if (field === "questionTextHinglish") {
      updated[idx].questionText_i18n = {
        ...(updated[idx].questionText_i18n || {}),
        hinglish: value,
      };
    } else {
      updated[idx][field] = value;
    }
    if (field === "questionType" && !["dropdown", "multiple-choice"].includes(value)) {
      updated[idx].optionRows = [];
    }
    setQuestions(updated);
  };

  const handleOptionChange = (qIdx, oIdx, field, value) => {
    const updated = [...questions];
    updated[qIdx].optionRows[oIdx][field] = value;
    setQuestions(updated);
  };

  const addOption = (qIdx) => {
    const updated = [...questions];
    updated[qIdx].optionRows.push({ en: "", hinglish: "" });
    setQuestions(updated);
  };

  const removeOption = (qIdx, oIdx) => {
    const updated = [...questions];
    updated[qIdx].optionRows.splice(oIdx, 1);
    setQuestions(updated);
  };

  const addQuestion = () => {
    setQuestions([...questions, createEmptyQuestion()]);
  };

  const removeQuestion = (idx) => {
    const updated = [...questions];
    updated.splice(idx, 1);
    setQuestions(updated);
  };

  const hasInvalidQuestionnaire = () => {
    if (!newFormName.trim()) {
      setErrorMessage("Please provide a form name for the questionnaire.");
      return true;
    }

    for (const question of questions) {
      const englishText =
        question.questionText_i18n?.en?.trim() || question.questionText?.trim();

      if (!englishText) {
        setErrorMessage("Each question must have English question text.");
        return true;
      }

      if (["dropdown", "multiple-choice"].includes(question.questionType)) {
        const validOptions = (question.optionRows || [])
          .map((option) => option.en?.trim())
          .filter(Boolean);

        if (validOptions.length < 2) {
          setErrorMessage(
            "Dropdown and multiple-choice questions need at least 2 options."
          );
          return true;
        }
      }
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let formQuestionairId = form.form_questionair;

      if (form.is_form_applicable) {
        if (formMode === "existing") {
          if (!form.form_questionair) {
            throw new Error("Please select an existing questionnaire form.");
          }
        } else {
          if (hasInvalidQuestionnaire()) {
            setLoading(false);
            return;
          }

          const preparedQuestions = questions.map(buildQuestionPayload);

          const createFormResponse = await fetch(CREATE_FORMS, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              form_name: newFormName,
              questions: preparedQuestions,
            }),
          });

          const createFormData = await createFormResponse.json();
          if (!createFormResponse.ok || !createFormData?.success) {
            throw new Error(
              createFormData?.message || "Failed to create questionnaire form"
            );
          }

          formQuestionairId =
            createFormData?.data?._id || createFormData?.data?.id || "";

          if (!formQuestionairId) {
            throw new Error("Form created but form id was not returned by server.");
          }
        }
      }

      const payload = {
        ...form,
        form_questionair: form.is_form_applicable ? formQuestionairId : "",
      };

      await axios.post(CREATE_RESEARCH, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate("/researches");
    } catch (err) {
      setErrorMessage(err?.message || "Failed to create research");
    }
    setLoading(false);
  };

  return (
    <React.Fragment>
      <div className="right-aside">
        <Helmet>Create Project</Helmet>
        <Row>
          <Col md={!toggleNotification ? 12 : 8} sm={12}>
            <Header />
            <div className={styles.main_section}>
              <div className={styles.headerSection}>
                <h6>Add Research</h6>
              </div>
              <Stepper
                steps={[{ label: "Basic Info" }, { label: "Advanced" }]}
                activeStep={step}
                activeColor="#1d7eeb"
              />
              <Form onSubmit={handleSubmit}>
                {errorMessage && (
                  <div className="text-danger mb-3">{errorMessage}</div>
                )}
                {step === 0 && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Research Name *</Form.Label>
                      <Form.Control
                        name="research_name"
                        value={form.research_name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Type *</Form.Label>
                      <Form.Select
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {RESEARCH_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Description *</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        required
                        rows={4}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Is Age Limited?"
                        name="is_age_limited"
                        checked={form.is_age_limited}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    {form.is_age_limited && (
                      <Row>
                        <Col>
                          <Form.Group>
                            <Form.Label>Min Age</Form.Label>
                            <Form.Control
                              type="number"
                              name="min_age"
                              value={form.age_limit.min_age}
                              onChange={handleChange}
                              min={0}
                            />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group>
                            <Form.Label>Max Age</Form.Label>
                            <Form.Control
                              type="number"
                              name="max_age"
                              value={form.age_limit.max_age}
                              onChange={handleChange}
                              min={0}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                    <div className={styles.btn_section}>
                      <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                        Cancel
                      </Button>
                      <Button type="button" onClick={handleNext}>Next</Button>
                    </div>
                  </>
                )}
                {step === 1 && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Duration (minutes) *</Form.Label>
                      <Form.Control
                        name="duration"
                        type="number"
                        value={form.duration}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Category *</Form.Label>
                      <Form.Select
                        name="category"
                        value={form.category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Location *</Form.Label>
                      <Form.Control
                        name="location"
                        value={form.location}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Price *</Form.Label>
                      <Form.Control
                        name="price"
                        type="number"
                        value={form.price}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Max Applications <small>(Accept applications up to this number)*</small></Form.Label>
                      <Form.Control
                        name="max_applications"
                        type="number"
                        value={form.max_applications}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Status"
                        name="status"
                        checked={form.status}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Is Form Applicable?"
                        name="is_form_applicable"
                        checked={form.is_form_applicable}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    {form.is_form_applicable && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Questionnaire Source</Form.Label>
                          <Form.Select
                            value={formMode}
                            onChange={(e) => setFormMode(e.target.value)}
                          >
                            <option value="existing">Select Existing Form</option>
                            <option value="new">Create New Form Here</option>
                          </Form.Select>
                        </Form.Group>

                        {formMode === "existing" ? (
                          <Form.Group className="mb-3">
                            <Form.Label>Form Questionnaire</Form.Label>
                            <Form.Select
                              name="form_questionair"
                              value={form.form_questionair}
                              onChange={handleChange}
                            >
                              <option value="">Select Form</option>
                              {forms.map((f) => (
                                <option key={f._id} value={f._id}>
                                  {f.form_name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        ) : (
                          <div className="border rounded p-3 mb-3">
                            <h6 className="mb-3">Create Questionnaire</h6>

                            <Form.Group className="mb-3">
                              <Form.Label>Form Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={newFormName}
                                onChange={(e) => setNewFormName(e.target.value)}
                                placeholder="Enter questionnaire name"
                              />
                            </Form.Group>

                            {questions.map((q, idx) => (
                              <div key={idx} className={styles.questionCard}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className={styles.questionTitle}>Question {idx + 1}</span>
                                  {questions.length > 1 && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      type="button"
                                      onClick={() => removeQuestion(idx)}
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </div>

                                <Row className="g-3">
                                  <Col md={6} sm={12}>
                                    <Form.Group>
                                      <Form.Label>Question Text (English)</Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={q.questionText_i18n?.en || q.questionText || ""}
                                        onChange={(e) =>
                                          handleQuestionChange(
                                            idx,
                                            "questionTextEn",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </Form.Group>
                                  </Col>

                                  <Col md={6} sm={12}>
                                    <Form.Group>
                                      <Form.Label>Question Text (Hinglish)</Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={q.questionText_i18n?.hinglish || ""}
                                        onChange={(e) =>
                                          handleQuestionChange(
                                            idx,
                                            "questionTextHinglish",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Optional"
                                      />
                                    </Form.Group>
                                  </Col>

                                  <Col md={6} sm={12}>
                                    <Form.Group>
                                      <Form.Label>Question Type</Form.Label>
                                      <Form.Select
                                        value={q.questionType}
                                        onChange={(e) =>
                                          handleQuestionChange(
                                            idx,
                                            "questionType",
                                            e.target.value
                                          )
                                        }
                                      >
                                        {questionTypes.map((type) => (
                                          <option key={type.value} value={type.value}>
                                            {type.label}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    </Form.Group>
                                  </Col>

                                  <Col md={6} sm={12}>
                                    <Form.Group>
                                      <Form.Label>Requirement</Form.Label>
                                      <Form.Select
                                        value={q.required !== false ? "required" : "optional"}
                                        onChange={(e) =>
                                          handleQuestionChange(
                                            idx,
                                            "required",
                                            e.target.value === "required"
                                          )
                                        }
                                      >
                                        <option value="required">Required</option>
                                        <option value="optional">Optional</option>
                                      </Form.Select>
                                    </Form.Group>
                                  </Col>
                                </Row>

                                {["dropdown", "multiple-choice"].includes(
                                  q.questionType
                                ) && (
                                  <div className={styles.optionsWrap}>
                                    <Form.Label className={styles.optionsLabel}>Options (English + Hinglish)</Form.Label>
                                    <Row className={`g-2 ${styles.optionHeader}`}>
                                      <Col md={5} sm={12}>English</Col>
                                      <Col md={5} sm={12}>Hinglish</Col>
                                      <Col md={2} sm={12}>Action</Col>
                                    </Row>

                                    {!q.optionRows.length && (
                                      <div className={styles.emptyOptions}>No options yet. Add at least 2 options.</div>
                                    )}

                                    {q.optionRows.map((opt, oIdx) => (
                                      <Row className={`g-2 mb-2 align-items-center ${styles.optionRow}`} key={oIdx}>
                                        <Col md={5} sm={12}>
                                          <Form.Control
                                            type="text"
                                            placeholder="English option"
                                            value={opt.en}
                                            onChange={(e) =>
                                              handleOptionChange(
                                                idx,
                                                oIdx,
                                                "en",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </Col>
                                        <Col md={5} sm={12}>
                                          <Form.Control
                                            type="text"
                                            placeholder="Hinglish option (optional)"
                                            value={opt.hinglish}
                                            onChange={(e) =>
                                              handleOptionChange(
                                                idx,
                                                oIdx,
                                                "hinglish",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </Col>
                                        <Col md={2} sm={12}>
                                          <Button
                                            variant="outline-danger"
                                            className={styles.optionRemoveBtn}
                                            type="button"
                                            onClick={() => removeOption(idx, oIdx)}
                                            title="Remove option"
                                          >
                                            &times;
                                          </Button>
                                        </Col>
                                      </Row>
                                    ))}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      type="button"
                                      onClick={() => addOption(idx)}
                                    >
                                      Add Option
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}

                            <Button variant="outline-success" type="button" onClick={addQuestion}>
                              Add Question
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                    <div className={styles.btn_section}>
                      <Button type="button" variant="secondary" onClick={handleBack}>
                        Back
                      </Button>
                      <Button type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Create"}
                      </Button>
                    </div>
                  </>
                )}
              </Form>
            </div>
          </Col>
          {toggleNotification && <Col md={4} sm={12}>
            <Notification />
          </Col>}
        </Row>
      </div>
    </React.Fragment>
  );
};

export default CreateProject;
